/**
 * useFeatureFlags Composable
 *
 * Read-only composable for checking global feature flags.
 * Used by both console and frontend apps.
 *
 * Features:
 * - Reactive computed properties that auto-update when flags change
 * - Generic isEnabled() method for any flag
 * - Named shortcuts for commonly used flags (add as needed)
 * - Loading and error states
 * - Manual refresh capability
 *
 * This composable does NOT include admin operations (create/update/delete).
 * For admin operations, use useFeatureFlagsAdmin (console only).
 *
 * @example
 * // In component setup()
 * const flags = useFeatureFlags()
 *
 * // Check any flag dynamically
 * const enabled = flags.isEnabled('communication_v2_enabled')
 *
 * // Use named shortcuts (recommended for common flags)
 * const showV2UI = flags.hasCommunicationV2
 *
 * // In template
 * <div v-if="flags.hasCommunicationV2.value">
 *   <CommunicationV2Panel />
 * </div>
 */

import { computed, type ComputedRef } from 'vue';
import { useFeatureFlagsStore } from '../store';

/**
 * Return type for useFeatureFlags composable
 */
export interface FeatureFlagsComposable {
  /**
   * Check if a feature flag is enabled (generic method)
   *
   * @param key - Feature flag key (snake_case)
   * @returns Computed boolean that updates reactively
   *
   * @example
   * const isEnabled = flags.isEnabled('new_feature')
   */
  isEnabled: (key: string) => ComputedRef<boolean>;

  /**
   * Named shortcut: Communication V2 feature
   * Check if new communication system is enabled
   */
  hasCommunicationV2: ComputedRef<boolean>;

  /**
   * Named shortcut: On-call routing V1 feature
   * Check if new scheduling-based on-call routing is enabled
   * @deprecated Use hasSchedulingV1 instead
   */
  hasOnCallRoutingV1: ComputedRef<boolean>;

  /**
   * Named shortcut: Scheduling V1 feature
   * Check if scheduling management (groups, lines) is enabled
   */
  hasSchedulingV1: ComputedRef<boolean>;

  /**
   * Named shortcut: Possible Evacuation feature
   * Check if evacuation notification flow is enabled
   */
  hasPossibleEvacuation: ComputedRef<boolean>;

  /**
   * Named shortcut: Migration Console V1 feature
   * Check if the entity-migration state console is enabled
   */
  hasMigrationConsoleV1: ComputedRef<boolean>;

  /**
   * Named shortcut: IVR Ops Dashboard feature
   * Check if the console IVR ops reports are enabled
   */
  hasIvrOpsDashboard: ComputedRef<boolean>;

  /**
   * Named shortcut: Admin MFA reset feature
   * Check if the console Users area (find a user, reset their MFA) is enabled
   */
  hasAuthAdminMfaReset: ComputedRef<boolean>;

  /**
   * Loading state (true during API calls)
   */
  loading: ComputedRef<boolean>;

  /**
   * Error message from last failed operation (null if no error)
   */
  error: ComputedRef<string | null>;

  /**
   * Check if flags have been fetched (cache is populated)
   */
  isCached: ComputedRef<boolean>;

  /**
   * Manually refresh flags from backend
   * Useful for admin UI "refresh" button
   */
  refresh: () => Promise<void>;
}

/**
 * Composable for checking global feature flags (read-only)
 *
 * Provides reactive access to feature flags without admin capabilities.
 * Tree-shaking friendly: Frontend apps only include read operations.
 *
 * @returns Feature flags composable with reactive flag checks
 *
 * @example
 * // Basic usage
 * const flags = useFeatureFlags()
 * const isEnabled = flags.isEnabled('my_feature')
 *
 * @example
 * // Using named shortcuts
 * const flags = useFeatureFlags()
 * if (flags.hasCommunicationV2.value) {
 *   // Show v2 UI
 * }
 *
 * @example
 * // In Vue template
 * <template>
 *   <div v-if="flags.loading.value">Loading flags...</div>
 *   <div v-else-if="flags.hasCommunicationV2.value">
 *     <CommunicationV2Panel />
 *   </div>
 *   <div v-else>
 *     <CommunicationV1Panel />
 *   </div>
 * </template>
 */
export function useFeatureFlags(): FeatureFlagsComposable {
  const store = useFeatureFlagsStore();

  // =============================================================================
  // GENERIC FLAG CHECKING
  // =============================================================================

  /**
   * Check if any feature flag is enabled
   * Returns false if flag not found (safe default)
   */
  const isEnabled = (key: string): ComputedRef<boolean> => {
    return computed(() => store.isEnabled(key));
  };

  // =============================================================================
  // NAMED SHORTCUTS (add more as needed)
  // =============================================================================

  /**
   * Communication V2 feature flag
   * Controls whether new communication system is available
   */
  const hasCommunicationV2 = computed<boolean>(() => {
    return store.isEnabled('communication_v2');
  });

  /**
   * On-call routing V1 feature flag
   * Controls whether scheduling-based on-call routing is available
   */
  const hasOnCallRoutingV1 = computed<boolean>(() => {
    return store.isEnabled('on_call_routing_v1');
  });

  /**
   * Scheduling V1 feature flag
   * Controls whether scheduling management (groups, lines) is available
   */
  const hasSchedulingV1 = computed<boolean>(() => {
    return store.isEnabled('scheduling_v1');
  });

  /**
   * Possible Evacuation feature flag
   * Controls whether evacuation notification flow is available
   */
  const hasPossibleEvacuation = computed<boolean>(() => {
    return store.isEnabled('possible_evacuation');
  });

  /**
   * Migration Console V1 feature flag
   * Controls whether the entity-migration state console is available
   */
  const hasMigrationConsoleV1 = computed<boolean>(() => {
    return store.isEnabled('migration_console_v1');
  });

  /**
   * IVR Ops Dashboard feature flag
   * Controls whether the console IVR ops reports are available
   */
  const hasIvrOpsDashboard = computed<boolean>(() => {
    return store.isEnabled('ivr_ops_dashboard');
  });

  /**
   * Admin MFA reset feature flag (PRX-2160)
   * Controls whether the console Users area is available
   */
  const hasAuthAdminMfaReset = computed<boolean>(() => {
    return store.isEnabled('auth_admin_mfa_reset');
  });

  // TODO: Add more named shortcuts for commonly used flags:
  // const hasNewDashboardUI = computed(() => store.isEnabled('new_dashboard_ui'))
  // const hasAdvancedReporting = computed(() => store.isEnabled('advanced_reporting'))

  // =============================================================================
  // STATE & ACTIONS
  // =============================================================================

  const loading = computed(() => store.loading);
  const error = computed(() => store.error);
  const isCached = computed(() => store.isCached);

  /**
   * Manually refresh flags from backend
   * Forces a fresh fetch, bypassing cache
   */
  async function refresh(): Promise<void> {
    await store.fetchFlags(true);
  }

  // =============================================================================
  // RETURN PUBLIC API
  // =============================================================================

  return {
    // Generic flag checking
    isEnabled,

    // Named shortcuts
    hasCommunicationV2,
    hasOnCallRoutingV1,
    hasSchedulingV1,
    hasPossibleEvacuation,
    hasMigrationConsoleV1,
    hasIvrOpsDashboard,
    hasAuthAdminMfaReset,

    // State
    loading,
    error,
    isCached,

    // Actions
    refresh,
  };
}

export default useFeatureFlags;
